import { useState } from 'react'
import type { TenantBootstrapModel } from '@unified-lk/tenant-config'
import type { AuthSession } from '../../../../types/plans'
import { staffApi } from '../../../../api/staff'

export type DiagnocatMatchCandidate = {
  candidate_id: string
  candidate_label?: string | null
  score?: number
  birth_date_match?: boolean | null
}

export function DiagnocatLinkPrompt({
  bootstrap,
  session,
  lkPatientId,
  legacyPatientId,
  linked,
  status,
  candidates,
  onLinked,
}: {
  bootstrap: TenantBootstrapModel
  session: AuthSession
  lkPatientId: number
  legacyPatientId: number | null
  linked: boolean
  status: string
  candidates: DiagnocatMatchCandidate[]
  onLinked?: () => void
}) {
  const [pending, setPending] = useState(false)
  const [error, setError] = useState('')

  if (linked) {
    return (
      <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
        Связка с Diagnocat подтверждена — можно отправлять КТ.
      </p>
    )
  }

  if (candidates.length === 0) {
    return (
      <p className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
        {status === 'guessed_matches'
          ? 'Кандидаты для связки не найдены.'
          : 'Для отправки КТ нужна связка с Diagnocat.'}
      </p>
    )
  }

  const confirm = async (diagnocatUid: string) => {
    setPending(true)
    setError('')
    try {
      await staffApi.linkDiagnocatPatient(bootstrap, session, {
        lkPatientId,
        diagnocatUid,
        localPatientId: legacyPatientId ?? lkPatientId,
      })
      onLinked?.()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Ошибка связки')
    } finally {
      setPending(false)
    }
  }

  return (
    <div className="space-y-2 rounded-xl border border-amber-200 bg-amber-50/70 p-3">
      <strong className="text-sm text-slate-900">Подтвердите связку Diagnocat</strong>
      {error ? <p className="text-xs text-red-600">{error}</p> : null}
      {candidates.map((match) => (
        <div
          key={match.candidate_id}
          className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-white bg-white px-3 py-2"
        >
          <div>
            <strong className="text-sm">{match.candidate_label ?? match.candidate_id}</strong>
            {match.score != null ? (
              <span className="ml-2 text-xs text-amber-700">оценка {match.score}</span>
            ) : null}
          </div>
          <button
            type="button"
            className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-1 text-xs font-semibold text-sky-800 disabled:opacity-60"
            disabled={pending}
            onClick={() => void confirm(match.candidate_id)}
          >
            {pending ? 'Связываем…' : 'Подтвердить'}
          </button>
        </div>
      ))}
    </div>
  )
}
